import { generatePath, NavigateFunction } from "react-router-dom";
import { IBreadcrumbBarItem } from "component-library";
import { TFunction } from "i18n";
import {
  prepareBreakdownTitle,
  prepareClosingDate2,
  prepareLocation,
  prepareSent,
} from "shared/helpers/breakdownViewHelpers";
import { findFacetName } from "shared/helpers/facetHelpers";
import { DisabilityPreference, IBreakdown } from "shared/models/breakdown";
import PageKey from "config/PageKey";

import { IActionList, IActionListItem } from "models/common";
import {
  IBreakdownPageTranslation,
  ICommonTranslation,
} from "models/translation";
import {
  IPrepareBreakdownViewData,
  prepareRole,
} from "helpers/breakdownViewHelpers";
import defaultsWithFallback from "helpers/defaultsWithFallback";

import { ListItemTranslator } from "helpers/translationHelpers";
import { fallbackTranslation } from "services/api/fallbackData";
import {
  IBreakdownViewRole,
  IBreakdownViewTexts,
} from "components/BreakdownView/BreakdownView";
import {
  breadcrumbsKeys,
  closingDateKeys,
  roleOptionLabelKeys,
} from "pages/JobPage/test.helpers";
import { BREAKDOWN_PRINT_FULL_PATH, MY_FILTERS_FULL_PATH } from "config/common";

export enum ActionListItemKey {
  Print = "Print",
  BackToJobsFeed = "BackToJobsFeed",
}

export const expanderKeys = {
  expand: "expand",
  collapse: "collapse",
};

const fallbackPageTranslation = fallbackTranslation[PageKey.Breakdown];
const fallbackCommonTranslation = fallbackTranslation.common;

export function prepareBreadcrumbs(
  pageTranslation: IBreakdownPageTranslation,
  accountUrl: string,
  navigate: NavigateFunction
): IBreadcrumbBarItem[] {
  const listItemTranslator = new ListItemTranslator(
    pageTranslation?.header?.breadcrumb,
    fallbackPageTranslation.header.breadcrumb
  );

  return [
    {
      url: accountUrl,
      text: listItemTranslator.get(breadcrumbsKeys.myAccount),
    },
    {
      url: MY_FILTERS_FULL_PATH,
      text: listItemTranslator.get(breadcrumbsKeys.jobsFeed),
      handleClick: (e) => {
        e.preventDefault();
        navigate(MY_FILTERS_FULL_PATH);
      },
    },
  ];
}

export function prepareBreakdownViewTexts(
  pageTranslation: IBreakdownPageTranslation,
  commonTranslation: ICommonTranslation,
  breakdown: IBreakdown,
  prepareBreakdownViewData: IPrepareBreakdownViewData
): IBreakdownViewTexts {
  const texts = defaultsWithFallback(
    pageTranslation?.view,
    fallbackPageTranslation.view
  );
  const expanderListItemTranslator = new ListItemTranslator(
    pageTranslation?.view?.viewRolesCollapserOptions,
    fallbackPageTranslation.view.viewRolesCollapserOptions
  );
  const closingDateListItemTranslator = new ListItemTranslator(
    pageTranslation?.view?.viewClosingDateLabels,
    fallbackPageTranslation.view.viewClosingDateLabels
  );
  const commonTexts = defaultsWithFallback(
    commonTranslation?.breakdowns,
    fallbackCommonTranslation.breakdowns
  );

  return {
    title: prepareBreakdownTitle(breakdown, {
      productionTypes: prepareBreakdownViewData.productionTypes,
      linkmailTypeAmendment: commonTexts.linkmailTypeAmendment,
    }),
    sent: prepareSent(
      {
        dateTimeFormat: commonTexts.dateTimeFormat,
        sentByAnonymous: texts.viewSentByAnonymous,
        sentByFormat: texts.viewSentByFormat,
        sentOnFormat: texts.viewSentOnFormat,
      },
      breakdown
    ),
    closingDate: prepareClosingDate2(
      {
        closed: closingDateListItemTranslator.get(closingDateKeys.closed),
        today: closingDateListItemTranslator.get(closingDateKeys.today),
        tomorrow: closingDateListItemTranslator.get(closingDateKeys.tomorrow),
        other: closingDateListItemTranslator.get(closingDateKeys.other),
        dateTimeFormat: commonTexts.dateTimeFormat,
      },
      breakdown.castingClosingDate,
      breakdown.freezeDate
    ),
    rolesSectionTitle: texts.viewRolesLabel.replace(
      "{roles}",
      String(breakdown.roles.length)
    ),
    productionLocation: prepareLocation(
      breakdown.production.location,
      breakdown.production.countryCode,
      prepareBreakdownViewData.countries
    ),
    castingLocation: prepareLocation(
      breakdown.castingLocation,
      breakdown.castingCountryCode,
      prepareBreakdownViewData.countries
    ),
    expander: {
      expandAll: expanderListItemTranslator.get(expanderKeys.expand),
      collapseAll: expanderListItemTranslator.get(expanderKeys.collapse),
    },
    attachmentsLabel: texts.viewAttachmentsLabel,
    projectSummaryLabel: texts.viewProjectSummaryLabel,
    roleDisabilityLabel: texts.viewRoleDisabilityLabel,
    roleGenderLabel: texts.viewRoleGenderLabel,
    castingDetailsLabel: texts.viewCastingDetailsLabel,
    rolePlayingAgeLabel: texts.viewRolePlayingAgeLabel,
    roleHeightLabel: texts.viewRoleHeightLabel,
    roleDisabilityInfoLabel: texts.viewRoleDisabilityInfoLabel,
    productionDatesLabel: texts.viewProductionDatesLabel,
    roleAnyHeight: texts.viewRoleAnyHeight,
    roleAppearanceLabel: texts.viewRoleAppearanceLabel,
    castingDetailsSectionLabel: texts.viewCastingDetailsSectionLabel,
    rolesLabel: texts.viewRolesLabel,
    roleContractTypeInfoLabel: texts.viewRoleContractTypeInfoLabel,
    roleAnyAges: texts.viewRoleAnyAges,
    castingLocationLabel: texts.viewCastingLocationLabel,
    productionLocationLabel: texts.viewProductionLocationLabel,
    productionDetailsSectionLabel: texts.viewProductionDetailsSectionLabel,
    sentByFormat: texts.viewSentByFormat,
    roleAttachmentsLabel: texts.viewRoleAttachmentsLabel,
    castingDirectorLabel: texts.viewCastingDirectorLabel,
    sentByAnonymous: texts.viewSentByAnonymous,
    roleContractTypeLabel: texts.viewRoleContractTypeLabel,
    message: texts.viewMessage,
    productionProducerLabel: texts.viewProductionProducerLabel,
    projectOverviewLabel: texts.viewProjectOverviewLabel,
    productionDirectorLabel: texts.viewProductionDirectorLabel,
    productionCompanyLabel: texts.viewProductionCompanyLabel,
    productionContractTypeLabel: texts.viewProductionContractTypeLabel,
    exclusiveText: commonTexts.viewExclusiveLabel,
    exclusiveTitle: commonTexts.viewExclusiveTooltip,
    productionContractType: findFacetName(
      prepareBreakdownViewData.contractTypes,
      breakdown.production.contractTypeId
    ),
    termsLabel: texts.viewTermsLabel,
    termsText: texts.viewTermsText,
    reportConcernButton: texts.viewRoleReportConcernButton,
    hiringPreferenceLabel: texts.hiringPreferenceLabel,
    localHireIsRequired: texts.localHireIsRequired,
    localHireDescription: texts.localHireDescription,
    localHireNoPreferences: texts.localHireNoPreferences,
    castingTypeLabel: texts.castingTypeLabel,
    castingTypeOnline: texts.castingTypeOnline,
    castingTypeSelfTape: texts.castingTypeSelfTape,
    castingTypeInPerson: texts.castingTypeInPerson,
    isRoleCastLabelText: commonTexts.isRoleCastLabelText,
  };
}

export interface IPrepareBreakdownViewRolesProps {
  pageTranslation: IBreakdownPageTranslation;
  breakdown: IBreakdown;
  prepareBreakdownViewData: IPrepareBreakdownViewData;
  t: TFunction;
  isYPPlayingAgeEnabled?: boolean;
}

export function prepareBreakdownViewRoles({
  pageTranslation,
  breakdown,
  prepareBreakdownViewData,
  t,
  isYPPlayingAgeEnabled,
}: IPrepareBreakdownViewRolesProps): IBreakdownViewRole[] {
  const texts = defaultsWithFallback(
    pageTranslation?.view,
    fallbackPageTranslation.view
  );
  const roleDisabilityListItemTranslator = new ListItemTranslator(
    pageTranslation.view?.viewRoleDisabilityOptions,
    fallbackPageTranslation.view.viewRoleDisabilityOptions
  );

  return breakdown.roles.map((role) =>
    prepareRole({
      breakdownRole: role,
      prepareBreakdownViewData,
      texts: {
        roleAnyPlayingAge: texts.viewRoleAnyAges,
        roleAnyHeight: texts.viewRoleAnyHeight,
        roleAnyGender: texts.viewRoleAnyGender,
        roleAnyAppearance: texts.viewRoleAnyAppearance,
        rolePlayingAgeFormat: texts.viewRolePlayingAgeFormat,
      },
      roleDisabilityTranslationMapping: {
        [DisabilityPreference.Undefined]: roleDisabilityListItemTranslator.get(
          roleOptionLabelKeys.undefined
        ),
        [DisabilityPreference.Any]: roleDisabilityListItemTranslator.get(
          roleOptionLabelKeys.any
        ),
        [DisabilityPreference.Specific]: roleDisabilityListItemTranslator.get(
          roleOptionLabelKeys.specific
        ),
        [DisabilityPreference.None]: roleDisabilityListItemTranslator.get(
          roleOptionLabelKeys.none
        ),
      },
      t,
      isYPPlayingAgeEnabled,
    })
  );
}

export const actionListLinkMappers = {
  [ActionListItemKey.Print]: (breakdownId: string): string => {
    return generatePath(BREAKDOWN_PRINT_FULL_PATH, {
      breakdownId,
    });
  },
};

export function prepareActionList(
  pageTranslation: IBreakdownPageTranslation,
  breakdownId: string
): IActionList {
  const items =
    pageTranslation?.header?.pageHeaderElements ||
    fallbackPageTranslation.header.pageHeaderElements;

  return {
    moreButtonText: "",
    items: items.map(
      (item): IActionListItem => ({
        variant: "primary",
        id: item.id,
        text: item.label,
        imageSourceUrl: item.icon,
        target: item.id === ActionListItemKey.Print ? "_blank" : undefined,
        linkUrl: actionListLinkMappers[item.id]?.(breakdownId) ?? "#",
      })
    ),
  };
}
